import React from "react";
import { Container, Grid, Typography, Box, Card } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import TerminalIcon from "@mui/icons-material/Terminal";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand, gradients } from "../theme";

const pillars = [
  {
    no: "01",
    icon: <SchoolIcon />,
    title: "Technology Training & Workforce Development",
    desc: "Intensive software engineering bootcamps delivered inside university campuses — immersing students in production workflows, advanced application logic, and testing routines so they confidently clear complex placement screening panels.",
    points: ["Campus bootcamps", "Placement readiness", "Production workflows"],
  },
  {
    no: "02",
    icon: <TerminalIcon />,
    title: "Custom Software Development",
    desc: "Our corporate software cell delivers end-to-end custom application engineering for global clients — scalable web apps, multi-tier enterprise systems, relational & non-relational databases, and seamless legacy cloud migrations.",
    points: ["Web & enterprise apps", "Database architecture", "Cloud migration"],
  },
  {
    no: "03",
    icon: <ArchitectureIcon />,
    title: "Advanced Product Engineering",
    desc: "Robust architectural guidance for startups and SMBs — converting raw business models into functional MVPs, managing cross-tenant boundaries, building secure authentication, and scaling multi-tenant SaaS systems.",
    points: ["MVP engineering", "Secure auth", "Multi-tenant SaaS"],
  },
  {
    no: "04",
    icon: <PsychologyIcon />,
    title: "AI Innovation & Deep Tech Labs",
    desc: "Operating at the edge of digital engineering — designing RAG applications, implementing fine-tuned foundational models, structuring intelligent prompt chains, and developing secure edge logic for industrial IoT.",
    points: ["RAG systems", "Fine-tuned models", "Edge & IoT"],
  },
];

const ServicesCard = () => {
  const ref = useReveal();
  return (
    <section id="services-section" className="services-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: "#fff" }}>
        <Container>
          <SectionHeading
            eyebrow="WHAT WE DO"
            title="Four Pillars of Technical Enablement"
            subtitle="Four synchronised business divisions delivering complete capability-building systems for academia and industry alike."
          />
          <Grid container spacing={3} className="reveal-stagger">
            {pillars.map((p) => (
              <Grid item xs={12} sm={6} key={p.no}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: { xs: 3, md: 4 },
                    position: "relative",
                    border: "1px solid rgba(12,131,200,0.12)",
                    overflow: "hidden",
                    transition: "transform .3s ease, box-shadow .3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 22px 48px rgba(15,39,64,0.12)",
                      "& .pillar-no": { color: "rgba(12,131,200,0.16)" },
                    },
                  }}
                >
                  <Typography
                    className="pillar-no"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 20,
                      fontSize: "4.5rem",
                      fontWeight: 800,
                      color: "rgba(12,131,200,0.07)",
                      transition: "color .3s ease",
                      lineHeight: 1,
                    }}
                  >
                    {p.no}
                  </Typography>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: gradients.brand,
                      color: "#fff",
                      mb: 2.5,
                      "& svg": { fontSize: 30 },
                    }}
                  >
                    {p.icon}
                  </Box>
                  <Typography variant="h6" sx={{ color: brand.navy, mb: 1.5, fontWeight: 700, pr: 6 }}>
                    {p.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.slate, mb: 2.5 }}>
                    {p.desc}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {p.points.map((pt) => (
                      <Box
                        key={pt}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 8,
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: brand.blueDark,
                          bgcolor: "rgba(12,131,200,0.08)",
                        }}
                      >
                        {pt}
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </section>
  );
};

export default ServicesCard;
